import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  CardActions,
  Typography,
  TextField,
  InputAdornment,
  Avatar,
  Chip,
  Button,
  IconButton,
  Paper,
  Skeleton,
  useTheme,
  alpha,
  Fade,
  Zoom
} from '@mui/material';

function StudentList() {
    const [students, setStudents] = useState([]);
    const [filteredStudents, setFilteredStudents] = useState([]);
    const [allStudents, setAllStudents] = useState([]);
    const [studentsLoaded, setStudentsLoaded] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedGrade, setSelectedGrade] = useState(null);
    const [loading, setLoading] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);
    const [showAll, setShowAll] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalFilteredCount, setTotalFilteredCount] = useState(0);
    const [weekSummaries, setWeekSummaries] = useState({}); // { [studentId]: { current: {...}, previous: {...} } }
    const theme = useTheme();
    const navigate = useNavigate();
    const studentsPerPage = 50;

    // Available grade levels
    const gradeOptions = [
        { id: 1, label: '6th Grade', value: 6 },
        { id: 2, label: '7th Grade', value: 7 },
        { id: 3, label: '8th Grade', value: 8 },
        { id: 4, label: '9th Grade', value: 9 },
        { id: 5, label: '10th Grade', value: 10 },
        { id: 6, label: '11th Grade', value: 11 },
        { id: 7, label: '12th Grade', value: 12 }
    ];

    // Debounced search function
    const debounceTimeout = React.useRef(null);

    // Load all students once and filter client-side for better performance
    const loadAllStudents = async () => {
        if (studentsLoaded) return allStudents;
        
        setLoading(true);
        try {
            const response = await axios.get('/students/api/list/');
            const data = response.data;
            setAllStudents(data);
            setStudentsLoaded(true);
            return data;
        } catch (error) {
            console.error('Error fetching students:', error);
            setAllStudents([]);
            return [];
        } finally {
            setLoading(false);
        }
    };

    const filterStudents = (query = '', gradeId = null, page = 1, studentsData = null) => {
        const dataToUse = studentsData || allStudents;
        console.log('filterStudents called with:', { query, gradeId, page, allStudentsLength: dataToUse.length });
        
        if (!dataToUse || dataToUse.length === 0) {
            console.log('No students loaded yet');
            return { results: [], total: 0, totalPages: 0 };
        }
        
        let filtered = [...dataToUse];
        
        // Apply search filter
        if (query && query.length >= 3) {
            const searchTerm = query.toLowerCase();
            filtered = filtered.filter(student => {
                const fullName = `${student.first_name} ${student.last_name}`.toLowerCase();
                const email = student.email?.toLowerCase() || '';
                const actualGrade = getActualGrade(student.grade_level)?.toString() || '';
                const gradeLabel = getGradeLabel(student.grade_level)?.toLowerCase() || '';
                
                return fullName.includes(searchTerm) || 
                       email.includes(searchTerm) || 
                       actualGrade.includes(searchTerm) ||
                       gradeLabel.includes(searchTerm);
            });
        }
        
        // Apply grade filter
        if (gradeId) {
            console.log('Filtering by grade:', gradeId);
            filtered = filtered.filter(student => student.grade_level === gradeId);
            console.log('Students after grade filter:', filtered.length);
        }
        
        // Apply pagination
        const totalPages = Math.ceil(filtered.length / studentsPerPage);
        const startIndex = (page - 1) * studentsPerPage;
        const endIndex = startIndex + studentsPerPage;
        const paginatedResults = filtered.slice(startIndex, endIndex);
        
        console.log('Final results:', { totalFiltered: filtered.length, paginatedCount: paginatedResults.length, totalPages });
        
        setStudents(paginatedResults);
        setFilteredStudents(paginatedResults);
        setTotalPages(totalPages);
        setTotalFilteredCount(filtered.length);
        setHasSearched(true);

        return {
            results: paginatedResults,
            total: filtered.length,
            totalPages
        };
    };

    // No initial data loading - students load only when searched

    const handleSearch = (e) => {
        const query = e.target.value;
        setSearchQuery(query);
        setCurrentPage(1);
        
        // Clear debounce timeout
        if (debounceTimeout.current) {
            clearTimeout(debounceTimeout.current);
        }
        
        // Debounce search for better UX
        debounceTimeout.current = setTimeout(async () => {
            if (query.length >= 3 || selectedGrade || showAll) {
                // Load students if not already loaded
                if (!studentsLoaded) {
                    await loadAllStudents();
                }
                filterStudents(query, selectedGrade, 1);
            } else if (query.length === 0 && !selectedGrade && !showAll) {
                // Clear results if no search criteria
                setStudents([]);
                setFilteredStudents([]);
                setTotalFilteredCount(0);
                setHasSearched(false);
            }
        }, 300);
    };

    const handleGradeFilter = async (gradeId) => {
        console.log('handleGradeFilter called with gradeId:', gradeId);
        const isDeselecting = gradeId === selectedGrade;
        
        setCurrentPage(1);
        setSearchQuery('');
        setShowAll(false);
        
        if (isDeselecting) {
            // Deselecting current grade
            console.log('Deselecting grade');
            setSelectedGrade(null);
            setStudents([]);
            setFilteredStudents([]);
            setTotalFilteredCount(0);
            setHasSearched(false);
        } else {
            // Selecting new grade
            console.log('Selecting grade:', gradeId);
            setSelectedGrade(gradeId);
            setLoading(true);
            
            try {
                // Always load students first if not loaded
                let studentsData = allStudents;
                if (!studentsLoaded) {
                    console.log('Loading all students first...');
                    studentsData = await loadAllStudents();
                } else {
                    studentsData = allStudents;
                }
                // Filter with the fresh data
                console.log('Filtering students for grade:', gradeId);
                filterStudents('', gradeId, 1, studentsData);
            } catch (error) {
                console.error('Error filtering by grade:', error);
            } finally {
                setLoading(false);
            }
        }
    };

    const handleShowAll = async () => {
        console.log('handleShowAll called');
        setShowAll(true);
        setSelectedGrade(null);
        setSearchQuery('');
        setCurrentPage(1);
        setLoading(true);
        
        try {
            // Always load students first
            const allStudentsData = await loadAllStudents();
            // Then filter with all students (no grade filter, no search query)
            filterStudents('', null, 1, allStudentsData);
        } catch (error) {
            console.error('Error loading all students:', error);
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (newPage) => {
        setCurrentPage(newPage);
        filterStudents(searchQuery, selectedGrade, newPage);
    };

    // Load week summaries for currently visible students
    useEffect(() => {
        const loadSummaries = async () => {
            try {
                if (!filteredStudents || filteredStudents.length === 0) {
                    setWeekSummaries({});
                    return;
                }
                const ids = filteredStudents.map(s => s.id);
                const res = await axios.post('/attendance/week-summaries/', { student_ids: ids });
                setWeekSummaries(res.data || {});
            } catch (e) {
                // non-fatal
            }
        };
        loadSummaries();
    }, [filteredStudents]);

    const getInitials = (firstName, lastName) => {
        return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
    };

    // Map GradeLevel IDs (1-7) to actual grade values (6-12)
    const getActualGrade = (gradeLevelId) => {
        const gradeMapping = {
            1: 6,   // ID 1 = 6th Grade
            2: 7,   // ID 2 = 7th Grade
            3: 8,   // ID 3 = 8th Grade
            4: 9,   // ID 4 = 9th Grade
            5: 10,  // ID 5 = 10th Grade
            6: 11,  // ID 6 = 11th Grade
            7: 12   // ID 7 = 12th Grade
        };
        return gradeMapping[gradeLevelId] || gradeLevelId;
    };

    const getGradeColor = (gradeLevelId) => {
        const actualGrade = getActualGrade(gradeLevelId);
        const colors = {
            6: '#FF6B6B', 7: '#4ECDC4', 8: '#45B7D1',
            9: '#96CEB4', 10: '#FFEAA7', 11: '#DDA0DD', 12: '#98D8C8'
        };
        return colors[actualGrade] || theme.palette.primary.main;
    };

    const getGradeLabel = (gradeLevelId) => {
        if (!gradeLevelId) return 'Unknown Grade';
        
        const actualGrade = getActualGrade(gradeLevelId);
        const gradeLabels = {
            6: '6th Grade',
            7: '7th Grade', 
            8: '8th Grade',
            9: '9th Grade',
            10: '10th Grade',
            11: '11th Grade',
            12: '12th Grade'
        };
        
        return gradeLabels[actualGrade] || `Grade ${actualGrade}`;
    };

    const renderSkeletonCards = () => (
        Array.from({ length: 8 }).map((_, index) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                <Card sx={{ height: '100%' }}>
                    <CardContent>
                        <Box display="flex" alignItems="center" mb={2}>
                            <Skeleton variant="circular" width={40} height={40} />
                            <Box ml={2} flex={1}>
                                <Skeleton variant="text" width="80%" />
                                <Skeleton variant="text" width="60%" />
                            </Box>
                        </Box>
                        <Skeleton variant="text" width="100%" />
                        <Skeleton variant="rectangular" width="60%" height={24} sx={{ mt: 1 }} />
                    </CardContent>
                    <CardActions>
                        <Skeleton variant="rectangular" width={80} height={32} />
                        <Skeleton variant="rectangular" width={60} height={32} />
                    </CardActions>
                </Card>
            </Grid>
        ))
    );

    const renderEmptyState = () => {
        if (!hasSearched && !selectedGrade && !showAll) {
            // Initial state - no search performed
            return (
                <Fade in={true}>
                    <Paper
                        sx={{
                            p: 8,
                            textAlign: 'center',
                            backgroundColor: alpha(theme.palette.primary.main, 0.02),
                            border: `1px dashed ${alpha(theme.palette.primary.main, 0.3)}`
                        }}
                    >
                        <Box 
                            sx={{ 
                                fontSize: 100, 
                                color: alpha(theme.palette.primary.main, 0.3),
                                mb: 3,
                                display: 'flex',
                                justifyContent: 'center'
                            }} 
                        >
                            🔍
                        </Box>
                        <Typography variant="h4" gutterBottom color="textPrimary" sx={{ fontWeight: 600 }}>
                            Find Students
                        </Typography>
                        <Typography variant="h6" color="textSecondary" sx={{ mb: 4 }}>
                            Search by name or email, select a grade level, or browse all students
                        </Typography>
                        
                        <Box sx={{ mb: 4 }}>
                            <Typography variant="body1" color="textSecondary" sx={{ mb: 2 }}>
                                💡 <strong>Quick Tips:</strong>
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                • Type at least 3 characters to search by name or email<br/>
                                • Click a grade level button for quick filtering<br/>
                                • Use "Show All" to browse the complete directory
                            </Typography>
                        </Box>
                        
                        <Button
                            variant="outlined"
                            onClick={handleShowAll}
                            sx={{ 
                                textTransform: 'none',
                                fontWeight: 600,
                                px: 4,
                                py: 1.5
                            }}
                        >
                            📚 Show All Students
                        </Button>
                    </Paper>
                </Fade>
            );
        } else {
            // No results found after search
            return (
                <Fade in={true}>
                    <Paper
                        sx={{
                            p: 6,
                            textAlign: 'center',
                            backgroundColor: alpha(theme.palette.primary.main, 0.02),
                            border: `1px dashed ${alpha(theme.palette.primary.main, 0.3)}`
                        }}
                    >
                        <Box 
                            sx={{ 
                                fontSize: 80, 
                                color: alpha(theme.palette.primary.main, 0.3),
                                mb: 2,
                                display: 'flex',
                                justifyContent: 'center'
                            }} 
                        >
                            🤷‍♂️
                        </Box>
                        <Typography variant="h5" gutterBottom color="textSecondary">
                            No students found
                        </Typography>
                        <Typography variant="body1" color="textSecondary">
                            {searchQuery 
                                ? `No students match "${searchQuery}". Try a different search term.`
                                : selectedGrade 
                                ? `No students found in ${gradeOptions.find(g => g.id === selectedGrade)?.label}.`
                                : 'No students match your current filters.'
                            }
                        </Typography>
                    </Paper>
                </Fade>
            );
        }
    };

    return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
            {/* Header Section */}
            <Box mb={4}>
                <Typography 
                    variant="h3" 
                    component="h1" 
                    gutterBottom
                    sx={{
                        fontWeight: 700,
                        background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        mb: 1
                    }}
                >
                    Student Directory
                </Typography>
                <Typography variant="h6" color="textSecondary" gutterBottom>
                    {loading ? 'Loading...' : `${totalFilteredCount} student${totalFilteredCount !== 1 ? 's' : ''} found`}
                </Typography>
            </Box>

            {/* Search and Filter Section */}
            <Paper 
                elevation={0}
                sx={{ 
                    p: 3, 
                    mb: 4,
                    backgroundColor: alpha(theme.palette.primary.main, 0.02),
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
                }}
            >
                {/* Search Input */}
                <Box sx={{ mb: 3 }}>
                    <TextField
                        fullWidth
                        variant="outlined"
                        placeholder="Search by name or email (minimum 3 characters)..."
                        value={searchQuery}
                        onChange={handleSearch}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <span style={{ color: theme.palette.primary.main, fontSize: '1.2rem' }}>🔍</span>
                                </InputAdornment>
                            ),
                            sx: {
                                backgroundColor: 'white',
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: alpha(theme.palette.primary.main, 0.2)
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: theme.palette.primary.main
                                }
                            }
                        }}
                        sx={{ maxWidth: 600 }}
                    />
                </Box>

                {/* Grade Level Filter Buttons */}
                <Box>
                    <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: 'text.primary' }}>
                        📚 Filter by Grade Level:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {gradeOptions.map((grade) => (
                            <Chip
                                key={grade.id}
                                label={grade.label}
                                onClick={() => handleGradeFilter(grade.id)}
                                variant={selectedGrade === grade.id ? 'filled' : 'outlined'}
                                sx={{
                                    backgroundColor: selectedGrade === grade.id 
                                        ? getGradeColor(grade.id)
                                        : 'transparent',
                                    color: selectedGrade === grade.id 
                                        ? 'white'
                                        : getGradeColor(grade.id),
                                    borderColor: getGradeColor(grade.id),
                                    fontWeight: 600,
                                    '&:hover': {
                                        backgroundColor: selectedGrade === grade.id 
                                            ? getGradeColor(grade.id)
                                            : alpha(getGradeColor(grade.id), 0.1)
                                    },
                                    cursor: 'pointer'
                                }}
                            />
                        ))}
                        {(selectedGrade || searchQuery || showAll) && (
                            <Chip
                                label="Clear Filters"
                                onClick={() => {
                                    setSelectedGrade(null);
                                    setSearchQuery('');
                                    setShowAll(false);
                                    setStudents([]);
                                    setFilteredStudents([]);
                                    setTotalFilteredCount(0);
                                    setHasSearched(false);
                                }}
                                variant="outlined"
                                sx={{
                                    color: 'text.secondary',
                                    borderColor: 'text.secondary',
                                    '&:hover': {
                                        backgroundColor: alpha(theme.palette.text.secondary, 0.1)
                                    }
                                }}
                            />
                        )}
                    </Box>
                </Box>
            </Paper>

            {/* Results Summary */}
            {hasSearched && !loading && filteredStudents.length > 0 && (
                <Box sx={{ mb: 3 }}>
                    <Typography variant="body1" color="textSecondary">
                        Showing {filteredStudents.length} student{filteredStudents.length !== 1 ? 's' : ''}
                        {totalPages > 1 && ` (Page ${currentPage} of ${totalPages})`}
                        {selectedGrade && ` in ${gradeOptions.find(g => g.id === selectedGrade)?.label}`}
                        {searchQuery && ` matching "${searchQuery}"`}
                    </Typography>
                </Box>
            )}

            {/* Students Grid */}
            {loading ? (
                <Grid container spacing={3}>
                    {renderSkeletonCards()}
                </Grid>
            ) : filteredStudents.length === 0 ? (
                renderEmptyState()
            ) : (
                <>
                    <Grid container spacing={3}>
                        {filteredStudents.map((student, index) => (
                        <Grid item xs={12} sm={6} md={4} lg={3} key={student.id}>
                            <Zoom in={true} style={{ transitionDelay: `${index * 50}ms` }}>
                                <Card 
                                    sx={{ 
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        transition: 'all 0.3s ease-in-out',
                                        '&:hover': {
                                            transform: 'translateY(-4px)',
                                            boxShadow: theme.shadows[8]
                                        }
                                    }}
                                >
                                    <CardContent sx={{ flexGrow: 1 }}>
                                        {/* Student Header */}
                                        <Box display="flex" alignItems="center" mb={2}>
                                            <Avatar 
                                                sx={{ 
                                                    bgcolor: getGradeColor(student.grade_level),
                                                    width: 48,
                                                    height: 48,
                                                    fontSize: '1.2rem',
                                                    fontWeight: 600
                                                }}
                                            >
                                                {getInitials(student.first_name, student.last_name)}
                                            </Avatar>
                                            <Box ml={2} flex={1}>
                                                <Typography 
                                                    variant="h6" 
                                                    component="div"
                                                    sx={{ 
                                                        fontWeight: 600,
                                                        lineHeight: 1.2,
                                                        mb: 0.5
                                                    }}
                                                >
                                                    {student.first_name} {student.last_name}
                                                </Typography>
                                                <Chip 
                                                    label={getGradeLabel(student.grade_level)}
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: alpha(getGradeColor(student.grade_level), 0.1),
                                                        color: getGradeColor(student.grade_level),
                                                        fontWeight: 600,
                                                        fontSize: '0.75rem'
                                                    }}
                                                />
                                            </Box>
                                        </Box>

                                        {/* Student Info */}
                                        <Box display="flex" alignItems="center" mb={1}>
                                            <span 
                                                style={{ 
                                                    fontSize: 16, 
                                                    color: theme.palette.text.secondary,
                                                    marginRight: 8 
                                                }} 
                                            >
                                                ✉️
                                            </span>
                                            <Typography 
                                                variant="body2" 
                                                color="textSecondary"
                                                sx={{ 
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap'
                                                }}
                                            >
                                                {student.email}
                                            </Typography>
                                        </Box>

                                        {/* Inline Attendance Summary */}
                                        <Box sx={{ mt: 1 }}>
                                            {(() => {
                                                const summary = weekSummaries?.[String(student.id)] || {};
                                                const cur = summary.current || { P: 0, A: 0, T: 0, E: 0, total: 0 };
                                                const prev = summary.previous || { P: 0, A: 0, T: 0, E: 0, total: 0 };
                                                return (
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                        <Typography variant="caption" color="textSecondary">
                                                            This week (P/A/T/E): {cur.P}/{cur.A}/{cur.T}/{cur.E}
                                                        </Typography>
                                                        <Typography variant="caption" color="textSecondary">
                                                            Prev week (P/A/T/E): {prev.P}/{prev.A}/{prev.T}/{prev.E}
                                                        </Typography>
                                                    </Box>
                                                );
                                            })()}
                                        </Box>

                                        {student.homeroom_teacher && (
                                            <Box display="flex" alignItems="center">
                                                <span 
                                                    style={{ 
                                                        fontSize: 16, 
                                                        color: theme.palette.text.secondary,
                                                        marginRight: 8 
                                                    }} 
                                                >
                                                    🏫
                                                </span>
                                                <Typography variant="body2" color="textSecondary">
                                                    {student.homeroom_teacher}
                                                </Typography>
                                            </Box>
                                        )}
                                    </CardContent>

                                    <CardActions sx={{ p: 2, pt: 0 }}>
                                        <Button
                                            size="small"
                                            onClick={() => navigate(`/schedule/${student.id}`)}
                                            sx={{ 
                                                textTransform: 'none',
                                                fontWeight: 600
                                            }}
                                        >
                                            📅 Schedule
                                        </Button>
                                        <Button
                                            size="small"
                                            onClick={() => navigate(`/reports/v2?student_id=${student.id}`)}
                                            sx={{ 
                                                textTransform: 'none',
                                                fontWeight: 600
                                            }}
                                        >
                                            📋 Attendance
                                        </Button>
                                        <IconButton
                                            size="small"
                                            onClick={() => navigate(`/edit-student/${student.id}`)}
                                            sx={{ 
                                                color: 'text.secondary',
                                                '&:hover': {
                                                    color: theme.palette.primary.main
                                                },
                                                fontSize: '1.1rem'
                                            }}
                                            title="Edit Student"
                                        >
                                            ✏️
                                        </IconButton>
                                    </CardActions>
                                </Card>
                            </Zoom>
                        </Grid>
                        ))}
                    </Grid>
                    
                    {/* Pagination */}
                    {totalPages > 1 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                            <Paper sx={{ p: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Button
                                        disabled={currentPage === 1}
                                        onClick={() => handlePageChange(currentPage - 1)}
                                        sx={{ textTransform: 'none' }}
                                    >
                                        ← Previous
                                    </Button>
                                    
                                    <Typography variant="body2" color="textSecondary">
                                        Page {currentPage} of {totalPages}
                                    </Typography>
                                    
                                    <Button
                                        disabled={currentPage === totalPages}
                                        onClick={() => handlePageChange(currentPage + 1)}
                                        sx={{ textTransform: 'none' }}
                                    >
                                        Next →
                                    </Button>
                                </Box>
                            </Paper>
                        </Box>
                    )}
                </>
            )}
        </Container>
    );
}

export default StudentList;
